import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import { toast } from 'react-toastify';
import LayoutWithSidebar from '../components/LayoutWithSidebar';
import { fetchShops } from '../services/shopService';
import {
  createShopProduct,
  searchProducts,
  updateShopProduct,
  fetchShopProducts,
} from '../services/shopProductService';
import { fetchProductCategories } from '../services/productCategoryService';
import { fetchBrands } from '../services/brandService';

const defaultVariant = { name: '', price: '', stock: '', isActive: '1' };

export default function AddShopProduct() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const initialShopId = params.get('shopId') || '';
  const shopProductId = params.get('shopProductId');

  const [shopId, setShopId] = useState(initialShopId);
  const [shops, setShops] = useState([]);
  const [mode, setMode] = useState(shopProductId ? 'search' : 'search');
  const [productSearch, setProductSearch] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [form, setForm] = useState({
    customName: '',
    customDescription: '',
    variants: [],
  });
  const [manualForm, setManualForm] = useState({
    name: '',
    description: '',
    categoryId: '',
    brandId: '',
    variants: [{ ...defaultVariant }],
  });
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loadingProduct, setLoadingProduct] = useState(false);

  const isEditing = Boolean(shopProductId);

  useEffect(() => {
    fetchShops({ limit: 200 })
      .then((res) => {
        const rec = res.data?.data?.records || res.data?.data || [];
        setShops(rec);
        if (!initialShopId && rec.length) {
          setShopId(rec[0].id);
        }
      })
      .catch(() => toast.error('Failed to load shops'));
  }, [initialShopId]);

  useEffect(() => {
    const loadMeta = async () => {
      try {
        const [catRes, brandRes] = await Promise.all([
          fetchProductCategories({ limit: 200 }),
          fetchBrands({ limit: 200 }),
        ]);
        setCategories(catRes.data?.data?.records || catRes.data?.data || []);
        setBrands(brandRes.data?.data?.records || brandRes.data?.data || []);
      } catch (error) {
        toast.error('Failed to load category data');
      }
    };
    loadMeta();
  }, []);

  useEffect(() => {
    if (!shopProductId || !shopId) return;
    const loadShopProduct = async () => {
      try {
        setLoadingProduct(true);
        const res = await fetchShopProducts(shopId, { limit: 200 });
        const records = res.data?.data?.records || res.data?.data || [];
        const product = records.find((record) => String(record.id) === String(shopProductId));
        if (!product) {
          toast.error('Shop product not found');
          return;
        }
        setSelectedProduct(product.product || null);
        setProductSearch(product.product?.name || '');
        setForm({
          customName: product.customName || product.product?.name || '',
          customDescription: product.customDescription || '',
          variants: (product.variants || []).map((variant) => ({
            productVariantId: variant.productVariantId,
            name: variant.productVariant?.name || variant.name || '',
            price: variant.price != null ? Number(variant.price) : 0,
            stock: variant.stock != null ? Number(variant.stock) : 0,
            isActive: variant.isAvailable !== false,
          })),
        });
      } catch (error) {
        toast.error('Failed to load product details');
      } finally {
        setLoadingProduct(false);
      }
    };

    loadShopProduct();
  }, [shopProductId, shopId]);

  const handleSearchProduct = async (query) => {
    setProductSearch(query);
    if (query.length < 2) {
      setSuggestions([]);
      return;
    }
    try {
      const res = await searchProducts(query);
      setSuggestions(res.data?.data || []);
    } catch (error) {
      setSuggestions([]);
    }
  };

  const selectProduct = (product) => {
    setSelectedProduct(product);
    setForm({
      customName: product.name,
      customDescription: '',
      variants: (product.variants || []).map((variant) => ({
        productVariantId: variant.id,
        name: variant.name,
        price: variant.price || 0,
        stock: variant.stock || 0,
        isActive: true,
      })),
    });
    setSuggestions([]);
    setProductSearch(product.name);
  };

  const updateVariant = (index, field, value) => {
    setForm((prev) => {
      const arr = [...prev.variants];
      arr[index] = { ...arr[index], [field]: value };
      return { ...prev, variants: arr };
    });
  };

  const addVariant = () => {
    setForm((prev) => ({
      ...prev,
      variants: [...prev.variants, { name: '', price: 0, stock: 0, isActive: true }],
    }));
  };

  const removeVariant = (index) => {
    setForm((prev) => {
      const arr = [...prev.variants];
      arr.splice(index, 1);
      return { ...prev, variants: arr };
    });
  };

  const updateManualVariant = (index, field, value) => {
    setManualForm((prev) => {
      const arr = [...prev.variants];
      arr[index] = { ...arr[index], [field]: value };
      return { ...prev, variants: arr };
    });
  };

  const addManualVariant = () => {
    setManualForm((prev) => ({
      ...prev,
      variants: [...prev.variants, { ...defaultVariant }],
    }));
  };

  const removeManualVariant = (index) => {
    setManualForm((prev) => {
      const arr = [...prev.variants];
      arr.splice(index, 1);
      return { ...prev, variants: arr.length ? arr : [{ ...defaultVariant }] };
    });
  };

  const manualVariantValid = useMemo(
    () => manualForm.variants.every((variant) => (variant.name || '').trim()),
    [manualForm.variants],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!shopId) {
      toast.error('Select a shop');
      return;
    }

    try {
      if (mode === 'manual' && !isEditing) {
        if (!manualForm.name.trim()) {
          toast.error('Enter product name');
          return;
        }
        if (!manualForm.categoryId) {
          toast.error('Select category');
          return;
        }
        if (!manualVariantValid) {
          toast.error('Fill variant names');
          return;
        }

        await createShopProduct(shopId, {
          manualProduct: true,
          manualProductName: manualForm.name,
          manualProductDescription: manualForm.description,
          manualProductCategoryId: manualForm.categoryId,
          manualProductBrandId: manualForm.brandId,
          variants: JSON.stringify(
            manualForm.variants.map((variant) => ({
              name: variant.name,
              price: Number(variant.price || 0),
              stock: Number(variant.stock || 0),
              isActive: variant.isActive === '1',
              isAvailable: variant.isActive === '1',
            })),
          ),
        });
        toast.success('Shop product created');
        navigate('/shop-products');
        return;
      }

      if (!selectedProduct) {
        toast.error('Select a product');
        return;
      }

      const payload = {
        customName: form.customName,
        customDescription: form.customDescription,
        variants: JSON.stringify(
          form.variants.map((variant) => ({
            productVariantId: variant.productVariantId,
            name: variant.name,
            price: Number(variant.price || 0),
            stock: Number(variant.stock || 0),
            isActive: Boolean(variant.isActive),
            isAvailable: Boolean(variant.isActive),
          })),
        ),
      };

      if (isEditing) {
        await updateShopProduct(shopId, shopProductId, payload);
        toast.success('Shop product updated');
      } else {
        await createShopProduct(shopId, {
          productId: selectedProduct.id,
          ...payload,
        });
        toast.success('Shop product created');
      }
      navigate('/shop-products');
    } catch (error) {
      toast.error('Failed to save shop product');
    }
  };

  return (
    <LayoutWithSidebar>
      <div className="mb-4">
        <h2 className="text-xl mb-4">{isEditing ? 'Edit Shop Product' : 'Add Shop Product'}</h2>
        <form onSubmit={handleSubmit} className="space-y-6 max-w-3xl">
          <div>
            <label className="block mb-1">Select Shop</label>
            <select
              value={shopId}
              onChange={(e) => setShopId(e.target.value)}
              className="border px-3 py-2 rounded w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              disabled={isEditing && loadingProduct}
            >
              <option value="">Select shop</option>
              {shops.map((shop) => (
                <option key={shop.id} value={shop.id}>
                  {shop.name}
                </option>
              ))}
            </select>
          </div>

          {!isEditing && (
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={() => setMode('search')}
                className={`px-3 py-1.5 rounded border ${
                  mode === 'search'
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'bg-transparent text-gray-700 dark:text-gray-200'
                }`}
              >
                From Catalog
              </button>
              <button
                type="button"
                onClick={() => setMode('manual')}
                className={`px-3 py-1.5 rounded border ${
                  mode === 'manual'
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'bg-transparent text-gray-700 dark:text-gray-200'
                }`}
              >
                Manual Create
              </button>
            </div>
          )}

          {mode === 'manual' && !isEditing ? (
            <div className="space-y-4 border rounded p-4 bg-white dark:bg-gray-800">
              <div>
                <label className="block mb-1">Product Name</label>
                <input
                  value={manualForm.name}
                  onChange={(e) => setManualForm({ ...manualForm, name: e.target.value })}
                  className="border px-3 py-2 rounded w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block mb-1">Category</label>
                  <select
                    value={manualForm.categoryId}
                    onChange={(e) => setManualForm({ ...manualForm, categoryId: e.target.value })}
                    className="border px-3 py-2 rounded w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  >
                    <option value="">Select category</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block mb-1">Brand (optional)</label>
                  <select
                    value={manualForm.brandId}
                    onChange={(e) => setManualForm({ ...manualForm, brandId: e.target.value })}
                    className="border px-3 py-2 rounded w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  >
                    <option value="">Select brand</option>
                    {brands.map((brand) => (
                      <option key={brand.id} value={brand.id}>
                        {brand.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block mb-1">Description</label>
                <textarea
                  value={manualForm.description}
                  onChange={(e) => setManualForm({ ...manualForm, description: e.target.value })}
                  className="border px-3 py-2 rounded w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold">Variants</h3>
                  <button
                    type="button"
                    onClick={addManualVariant}
                    className="px-2 py-1 text-sm bg-green-600 text-white rounded"
                  >
                    Add Variant
                  </button>
                </div>
                {manualForm.variants.map((variant, index) => (
                  <div key={index} className="grid grid-cols-5 gap-2 mb-2 items-end">
                    <div className="col-span-2">
                      <label className="block mb-1">Name</label>
                      <input
                        value={variant.name}
                        onChange={(e) => updateManualVariant(index, 'name', e.target.value)}
                        className="border px-2 py-1 rounded w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block mb-1">Price</label>
                      <input
                        type="number"
                        value={variant.price}
                        onChange={(e) => updateManualVariant(index, 'price', e.target.value)}
                        className="border px-2 py-1 rounded w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block mb-1">Stock</label>
                      <input
                        type="number"
                        value={variant.stock}
                        onChange={(e) => updateManualVariant(index, 'stock', e.target.value)}
                        className="border px-2 py-1 rounded w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      />
                    </div>
                    <div className="flex items-center mt-6">
                      <select
                        value={variant.isActive}
                        onChange={(e) => updateManualVariant(index, 'isActive', e.target.value)}
                        className="border px-2 py-1 rounded w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      >
                        <option value="1">Active</option>
                        <option value="0">Inactive</option>
                      </select>
                    </div>
                    <div className="flex justify-center mt-6">
                      <button
                        type="button"
                        onClick={() => removeManualVariant(index)}
                        className="text-red-500"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-4 border rounded p-4 bg-white dark:bg-gray-800">
              <div>
                <label className="block mb-1">Search Product</label>
                <div className="relative">
                  <input
                    value={productSearch}
                    onChange={(e) => handleSearchProduct(e.target.value)}
                    className="border px-3 py-2 rounded w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    placeholder="Type to search"
                    disabled={isEditing}
                  />
                  {productSearch && !isEditing && (
                    <button
                      type="button"
                      onClick={() => {
                        setProductSearch('');
                        setSuggestions([]);
                        setSelectedProduct(null);
                      }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
                {suggestions.length > 0 && (
                  <ul className="border rounded mt-1 bg-white dark:bg-gray-700 max-h-40 overflow-y-auto">
                    {suggestions.map((suggestion) => (
                      <li
                        key={suggestion.id}
                        onClick={() => selectProduct(suggestion)}
                        className="px-3 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                      >
                        {suggestion.name}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {selectedProduct && (
                <>
                  <div>
                    <label className="block mb-1">Custom Name</label>
                    <input
                      value={form.customName}
                      onChange={(e) => setForm({ ...form, customName: e.target.value })}
                      className="border px-3 py-2 rounded w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block mb-1">Custom Description</label>
                    <textarea
                      value={form.customDescription}
                      onChange={(e) => setForm({ ...form, customDescription: e.target.value })}
                      className="border px-3 py-2 rounded w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold">Variants</h3>
                      <button
                        type="button"
                        onClick={addVariant}
                        className="px-2 py-1 text-sm bg-green-600 text-white rounded"
                      >
                        Add Variant
                      </button>
                    </div>
                    {form.variants.map((variant, index) => (
                      <div key={index} className="grid grid-cols-5 gap-2 mb-2 items-end">
                        <div>
                          <label className="block mb-1">Name</label>
                          <input
                            value={variant.name || ''}
                            onChange={(e) => updateVariant(index, 'name', e.target.value)}
                            className="border px-2 py-1 rounded w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          />
                        </div>
                        <div>
                          <label className="block mb-1">Price</label>
                          <input
                            type="number"
                            value={variant.price}
                            onChange={(e) => updateVariant(index, 'price', e.target.value)}
                            className="border px-2 py-1 rounded w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          />
                        </div>
                        <div>
                          <label className="block mb-1">Stock</label>
                          <input
                            type="number"
                            value={variant.stock}
                            onChange={(e) => updateVariant(index, 'stock', e.target.value)}
                            className="border px-2 py-1 rounded w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          />
                        </div>
                        <div className="flex items-center mt-6">
                          <input
                            id={`variant-active-${index}`}
                            type="checkbox"
                            checked={variant.isActive}
                            onChange={(e) => updateVariant(index, 'isActive', e.target.checked)}
                          />
                          <label htmlFor={`variant-active-${index}`} className="ml-2">
                            Active
                          </label>
                        </div>
                        <div className="flex justify-center mt-6">
                          <button
                            type="button"
                            onClick={() => removeVariant(index)}
                            className="text-red-500"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={() => navigate('/shop-products')}
              className="px-4 py-2 rounded border"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-indigo-600 text-white px-4 py-2 rounded"
              disabled={loadingProduct}
            >
              {isEditing ? 'Update' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </LayoutWithSidebar>
  );
}
